import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import { clampIsoDateToToday, getTodayIsoDate } from "../../../utils/reportDateLimits";
import Backdrop from "@mui/material/Backdrop";
import AppLoader from '../../../components/AppLoader/AppLoader';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import XLSX, { boldRows } from "utils/xlsxStyled";
import eOpsLogo from '../../../images/eops.png';
import ReportExportButtons from "../../Report/components/ReportExportButtons";
import "../../Report/reportPageLayout.scss";
import "../../Report/ChangeOfAttributesStatusReport.scss";
import "./SubdivisionWiseAPMSComplaintSummary.scss";
import FESCOLOGO from "../../../images/DISCO Logos/FESCO.png";
import GEPCOLOGO from "../../../images/DISCO Logos/GEPCO.png";
import HESCOLOGO from "../../../images/DISCO Logos/HESCO.png";
import IESCOLOGO from "../../../images/DISCO Logos/IESCO.png";
import MEPCOLOGO from "../../../images/DISCO Logos/MEPCO.png";
import LESCOLOGO from "../../../images/DISCO Logos/LESCO.jpg";
import PESCOLOGO from "../../../images/DISCO Logos/PESCO.png";
import QESCOLOGO from "../../../images/DISCO Logos/QESCO.png";
import SEPCOLOGO from "../../../images/DISCO Logos/SEPCO.png";
import TESCOLOGO from "../../../images/DISCO Logos/TESCO.png";
import { cfpBaseUrl } from '../../../network/urls';
import { convertDate, convertDatewithTime, getName, companyShortName } from '../../../services/Functions';
import '../../../style/common.scss';
import { resolveOfficeCodeForAdmin, resolveReportGoOfficeCode } from "../../../utils/adminCompany";
const normalizeApmsSubDivRow = (item) => {
  const resolvedInTime = Number(
    item.ResolvedInTime ?? item.resolvedInTime ?? 0
  );
  const resolvedLate = Number(item.ResolvedLate ?? item.resolvedLate ?? 0);
  const totalResolved = Number(
    item.Resolved ??
      item.resolved ??
      item.TotalResolved ??
      item.totalResolved ??
      resolvedInTime + resolvedLate
  );

  return {
    subDivisionCode: item.OfficeCode ?? item.officeCode ?? "-",
    total: Number(item.Total ?? item.total ?? 0),
    resolvedInTime,
    resolvedLate,
    totalResolved,
    pending: Number(item.Pending ?? item.pending ?? 0),
  };
};

const REPORT_LEVEL_OPTIONS = [
  { value: "1", label: "PITC " },
  { value: "2", label: "Company" },
  { value: "3", label: "Circle" },
  { value: "4", label: "Division" },
  { value: "5", label: "Sub-Division" },
  { value: "6", label: "Hierarchical" },
];

const HIERARCHICAL_REPORT_LEVEL = "6";

const normalizeUserHierarchy = (hierarchyLevel) => {
  const level = Number(hierarchyLevel);
  if (level === 52) {
    return 5;
  }
  return level;
};

const getEffectiveUserHierarchy = (hierarchyValue, cfcLevelId, userHierarchy, userCode) => {
  if (String(userCode) === "10") {
    return 1;
  }
  // TAC / Sub-Division users can come as hierarchy 5 or 52.
  if (Number(userHierarchy) === 52 || Number(userHierarchy) === 5) {
    return 5;
  }
  const raw = Number(hierarchyValue || cfcLevelId || userHierarchy) || 2;
  return normalizeUserHierarchy(raw);
};

const getDefaultReportLevel = (userCode, effectiveHierarchy) => {
  if (String(userCode) === "10") {
    return "1";
  }
  if (effectiveHierarchy >= 5) {
    return HIERARCHICAL_REPORT_LEVEL;
  }
  if (effectiveHierarchy === 4) {
    return "4";
  }
  if (effectiveHierarchy === 3) {
    return "3";
  }
  return "2";
};

const getAvailableReportLevelOptions = (userCode, effectiveHierarchy) => {
  if (String(userCode) === "10") {
    return REPORT_LEVEL_OPTIONS;
  }

  if (effectiveHierarchy === 2) {
    return REPORT_LEVEL_OPTIONS.filter((option) => option.value !== "1");
  }
  if (effectiveHierarchy === 3) {
    return REPORT_LEVEL_OPTIONS.filter((option) =>
      ["3", "4", "5", HIERARCHICAL_REPORT_LEVEL].includes(option.value),
    );
  }
  if (effectiveHierarchy === 4) {
    return REPORT_LEVEL_OPTIONS.filter((option) =>
      ["4", "5", HIERARCHICAL_REPORT_LEVEL].includes(option.value),
    );
  }
  if (effectiveHierarchy >= 5) {
    return REPORT_LEVEL_OPTIONS.filter((option) =>
      [HIERARCHICAL_REPORT_LEVEL].includes(option.value),
    );
  }

  return REPORT_LEVEL_OPTIONS.filter((option) => option.value !== "1");
};

const resolveAuthOfficeCode = (userCode, effectiveHierarchy) => {
  const authCode = String(userCode ?? "");
  if (!authCode) {
    return "";
  }
  if (effectiveHierarchy <= 1 || authCode === "10") {
    return authCode;
  }
  if (effectiveHierarchy === 2) {
    return authCode.slice(0, 2);
  }
  if (effectiveHierarchy === 3) {
    return authCode.slice(0, 3);
  }
  if (effectiveHierarchy === 4) {
    return authCode.slice(0, 4);
  }
  return authCode.slice(0, 5);
};

const isHierarchicalReportLevelValue = (reportLevelValue) =>
  Number(reportLevelValue) === Number(HIERARCHICAL_REPORT_LEVEL);

const resolveApmsReportOfficeCode = ({
  userCode,
  effectiveHierarchy,
  reportLevelValue,
  officeCode,
  selectedCompanyCode,
  selectedAdminCompanyCode,
  circleCode,
  selectedDivision,
  selectedSubDivision,
}) => {
  const isAdminUser = String(userCode) === "10";
  const isHierarchical = isHierarchicalReportLevelValue(reportLevelValue);

  if (isAdminUser) {
    if (!isHierarchical) {
      return (
        selectedCompanyCode ||
        String(selectedAdminCompanyCode || "") ||
        officeCode ||
        "10"
      );
    }
    return resolveReportGoOfficeCode({
      code: userCode,
      officeCode,
      selectedCompanyCode,
      selectedAdminCompanyCode,
      circleCode,
      selectedDivision,
      selectedSubDivision,
    });
  }

  if (!isHierarchical) {
    return resolveAuthOfficeCode(userCode, effectiveHierarchy);
  }

  // Sub-division auth (hierarchy 5 / 52): always use full 5-digit code.
  // Do not fall through to selectedDivision (4 digits) set for scoping.
  if (effectiveHierarchy >= 5) {
    return resolveAuthOfficeCode(userCode, effectiveHierarchy);
  }

  if (selectedSubDivision) {
    return selectedSubDivision;
  }
  if (selectedDivision) {
    return selectedDivision;
  }
  if (circleCode) {
    return circleCode;
  }

  return resolveAuthOfficeCode(userCode, effectiveHierarchy);
};

const getReportLevelColumnLabel = (reportLevelValue, officeCodeValue) => {
  const level = Number(reportLevelValue);
  if (level === 2) {
    return String(officeCodeValue) === "10" ? "DISCO Name" : "Company Name";
  }
  if (level === 3) return "Circle Name";
  if (level === 4) return "Division Name";
  return "Sub Division Name";
};

const buildUniqueCircles = (items = []) =>
  items.reduce((acc, curr) => {
    if (!acc.some((item) => item.circleCode === curr.circleCode)) {
      acc.push({
        circleCode: curr.circleCode,
        circleName: curr.circleName,
        companyCode: curr.companyCode,
      });
    }
    return acc;
  }, []);

const buildUniqueDivisions = (items = []) =>
  items.reduce((acc, item) => {
    if (!acc.some((division) => division.divisionCode === item.divisionCode)) {
      acc.push({
        divisionCode: item.divisionCode,
        divisionName: item.divisionName,
        companyCode: item.companyCode,
      });
    }
    return acc;
  }, []);

const buildUniqueSubDivisions = (items = []) =>
  items.reduce((acc, item) => {
    if (!acc.some((sub) => sub.subDivisionCode === item.subDivisionCode)) {
      acc.push({
        subDivisionCode: item.subDivisionCode,
        subDivisionName: item.subDivisionName,
        companyCode: item.companyCode,
      });
    }
    return acc;
  }, []);

const filterDivisionsByCircle = (items = [], circleCode) =>
  buildUniqueDivisions(
    items.filter((item) => String(item.circleCode) === String(circleCode)),
  );

const filterSubDivisionsByDivision = (items = [], divisionCode) =>
  buildUniqueSubDivisions(
    items.filter((item) => String(item.divisionCode) === String(divisionCode)),
  );

const filterSubDivisionsByCircle = (items = [], circleCode) =>
  buildUniqueSubDivisions(
    items.filter((item) => String(item.circleCode) === String(circleCode)),
  );

const applyScopedHierarchyFilterLists = ({
  items,
  effectiveHierarchy,
  authCode,
  setCircleCode,
  setSelectedDivision,
  setSelectedSubDivision,
  setFilteredCircles,
  setFilteredDivisions,
  setFilteredSubDivisions,
}) => {
  const scopedCode = String(authCode ?? "");

  if (effectiveHierarchy === 3) {
    const circle = scopedCode.slice(0, 3);
    setCircleCode?.(circle);
    setFilteredCircles?.(buildUniqueCircles(items));
    setFilteredDivisions?.(filterDivisionsByCircle(items, circle));
    setFilteredSubDivisions?.(filterSubDivisionsByCircle(items, circle));
    return;
  }

  if (effectiveHierarchy === 4) {
    const division = scopedCode.slice(0, 4);
    setSelectedDivision?.(division);
    setFilteredCircles?.(buildUniqueCircles(items));
    setFilteredDivisions?.(filterDivisionsByCircle(items, scopedCode.slice(0, 3)));
    setFilteredSubDivisions?.(filterSubDivisionsByDivision(items, division));
    return;
  }

  if (effectiveHierarchy >= 5) {
    const division = scopedCode.slice(0, 4);
    const subDivision = scopedCode.slice(0, 5);
    setSelectedDivision?.(division);
    setSelectedSubDivision?.(subDivision);
    setFilteredSubDivisions?.(filterSubDivisionsByDivision(items, division));
    return;
  }

  setFilteredCircles?.(buildUniqueCircles(items));
  setFilteredDivisions?.(buildUniqueDivisions(items));
  setFilteredSubDivisions?.(buildUniqueSubDivisions(items));
};

const resolveOfficeDisplayName = (officeCode, subDivisionsList = []) => {
  const codeStr = String(officeCode ?? "").trim();
  if (!codeStr || codeStr === "-") return "-";

  const name = getName(codeStr, subDivisionsList);
  const defaultLabels = new Set(["Company", "Circle", "Division", "Sub-Division"]);
  const resolvedName = name && !defaultLabels.has(name) ? name : "";

  if (codeStr.length === 2) {
    const company = companyShortName(codeStr);
    return company && company !== "DISTRIBUTION COMPANY NAME"
      ? `${company}-${codeStr}`
      : codeStr;
  }

  if (resolvedName) {
    return `${resolvedName}-${codeStr}`;
  }

  return codeStr;
};

const SubdivisionWiseAPMSComplaintSummary = () => {

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Adding 1 because months are 0-indexed
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const { token, code, name, roleName, roleTypeName, cscID, cfcLevelId, divisionCode, hierarchy: userHierarchy, selectedAdminCompanyCode } = useSelector(state => state.auth)
  const tableRef = useRef(null);
  const [assmntBatchWise, setAssmntBatchWise] = useState([]);
  // const { officeCode: offCode } = useSelector(state => state.officeCode)
  const offCode = userHierarchy == 1
    ? code
    : userHierarchy == 2
      ? code.slice(0, 2)
      : userHierarchy == 3
        ? code.slice(0, 3)
        : userHierarchy == 4
          ? code.slice(0, 4)
          : userHierarchy == 5 || userHierarchy == 52
            ? code.slice(0, 5)
            : code;
  const [tableData, setTableData] = useState([]);
  const [viewBackdrop, setViewBackdrop] = useState(false);
  const [apiData, setApiData] = useState([]);
  const [circleCode, setCircleCode] = useState('');
  const [circleName, setCircleName] = useState('');
  const [officeCode, setOfficeCode] = useState();
  const [officeName, setOfficeName] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedSubDivision, setSelectedSubDivision] = useState('');
  const [filteredDivisions, setFilteredDivisions] = useState([]);
  const [filteredSubDivisions, setFilteredSubDivisions] = useState([]);
  const [hierarchy, setHierarchy] = useState('');
  const [filteredCircles, setFilteredCircles] = useState([]);
const [isGoDisabled, setIsGoDisabled] = useState(true); 
  const [lastAppliedFilters, setLastAppliedFilters] = useState(null);
  const [appliedReportOfficeCode, setAppliedReportOfficeCode] = useState("");
  const [reportLevel, setReportLevel] = useState(() =>
    getDefaultReportLevel(code, normalizeUserHierarchy(Number(userHierarchy))),
  );
  const [appliedReportLevel, setAppliedReportLevel] = useState(() =>
    getDefaultReportLevel(code, normalizeUserHierarchy(Number(userHierarchy))),
  );
  const [dateFrom, setDateFrom] = useState(getCurrentDate())
  const [dateTo, setDateTo] = useState(getCurrentDate())
  const [totals, setTotals] = useState({});
  const [complaints, setComplaints] = useState([])

  const [subDivisions, setSubDivisions] = useState(JSON.parse(window.localStorage.getItem('subDivisions')) || []);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState('');

  useEffect(() => {
    if (String(code) === "10" && selectedAdminCompanyCode) {
      const company = String(selectedAdminCompanyCode);
      setSelectedCompanyCode(company);
      setOfficeCode(company);
    }
  }, [code, selectedAdminCompanyCode]);

  const [currentBillingStats, setCurrentBillingStats] = useState({
    batches: [],
    amountsBatchWise: [],
    unitsBatchWise: [],
    categories: ['Total', 'Dom.', 'Com.', 'Ind.', 'Arg.', 'Oth.'],
    amountsCategoryWise: [],
  })
  const OffLevel = String(officeCode).length;
  const [billingReportPopup, setBillingReportPopup] = useState(false);

  const companyCode = code.slice(0, 2)

  const getLogo = (code) => {
    switch (code) {
      case '11':
        return LESCOLOGO
      case '12':
        return GEPCOLOGO
      case '13':
        return FESCOLOGO
      case '14':
        return IESCOLOGO
      case '15':
        return MEPCOLOGO
      case '16':
        return PESCOLOGO
      case '27':
        return SEPCOLOGO
      case '37':
        return HESCOLOGO
      case '48':
        return QESCOLOGO
      case '59':
        return TESCOLOGO
      default:
        return MEPCOLOGO
    }
  }

  const getCompanies = (code) => {
    switch (code) {
      case '11':
        return "Lahore Electric Supply Company"
      case '12':
        return "Gujranwala Electric Power Company"
      case '13':
        return "Faisalabad Electric Supply Company"
      case '14':
        return "Islamabad Electric Supply Company"
      case '15':
        return "Multan Electric Power Company"
      case '16':
        return "Peshawar Electric Supply Company"
      case '27':
        return "Sukkur Electric Power Company"
      case '37':
        return "Hyderabad Electric Supply Company"
      case '48':
        return "Quetta Electric Supply Company"
      case '59':
        return "Tribal Electric Supply Company"
      default:
        return "Multan Electric Power Company"
    }
  }

  const displayCount = (value, showZero = false) => {
    const count = Number(value) || 0;
    if (!showZero && count === 0) {
      return "-";
    }
    return formatWithCommas(count);
  };

  const reportCompanyCode =
    selectedCompanyCode || selectedAdminCompanyCode || code.slice(0, 2);
  const companyTotalLabel = `${companyShortName(String(reportCompanyCode))} Total`;

  const resolveCurrentApiOfficeCode = () =>
    resolveReportGoOfficeCode({
      code,
      officeCode,
      selectedCompanyCode,
      selectedAdminCompanyCode,
      circleCode,
      selectedDivision,
      selectedSubDivision,
    });

  const officeCodeColumnLabel = "Office";

  const DownloadPdf = async (print) => {
    return new Promise((resolve) => {
      let offName = officeName || "MEPCO Region";
      const pdfOfficeCodeLabel = "Office";
      const title =
        dateFrom && dateTo && convertDate(dateFrom) === convertDate(dateTo)
          ? `APMS Resolved Complaint Stats as on ${convertDate(dateTo)}`
          : `APMS Resolved Complaint Stats from ${dateFrom ? convertDate(dateFrom) : "-"} to ${dateTo ? convertDate(dateTo) : "-"}`;
      const headers = [
        [
          pdfOfficeCodeLabel,
          "Total",
          "Resolved (In Time)",
          "Resolved (Late)",
          "Total Resolved",
          "Pending",
        ].map((header) => ({
          content: header,
          styles: { halign: "center", fontStyle: "bold" },
        })),
      ];

      const body = [];

      if (tableData && tableData.length > 0) {
        tableData.forEach((item) => {
          body.push([
            { content: item.officeDisplayName || item.subDivisionCode || "-", styles: { halign: "left" } },
            { content: displayCount(item.total, true), styles: { halign: "center" } },
            {
              content: displayCount(item.resolvedInTime),
              styles: { halign: "center", textColor: [22, 163, 74] },
            },
            {
              content: displayCount(item.resolvedLate),
              styles: { halign: "center", textColor: [220, 38, 38] },
            },
            {
              content: displayCount(item.totalResolved, true),
              styles: { halign: "center" },
            },
            {
              content: displayCount(item.pending, true),
              styles: { halign: "center", fontStyle: "bold" },
            },
          ]);
        });

        const totals = tableData.reduce(
          (acc, item) => ({
            total: acc.total + (item.total || 0),
            resolvedInTime: acc.resolvedInTime + (item.resolvedInTime || 0),
            resolvedLate: acc.resolvedLate + (item.resolvedLate || 0),
            totalResolved: acc.totalResolved + (item.totalResolved || 0),
            pending: acc.pending + (item.pending || 0),
          }),
          {
            total: 0,
            resolvedInTime: 0,
            resolvedLate: 0,
            totalResolved: 0,
            pending: 0,
          }
        );

        body.push([
          {
            content: companyTotalLabel,
            styles: { halign: "left", fontStyle: "bold", fillColor: "#f8f9fa" },
          },
          {
            content: displayCount(totals.total, true),
            styles: { halign: "center", fontStyle: "bold", fillColor: "#f8f9fa" },
          },
          {
            content: displayCount(totals.resolvedInTime, true),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: "#f8f9fa",
              textColor: [22, 163, 74],
            },
          },
          {
            content: displayCount(totals.resolvedLate, true),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: "#f8f9fa",
              textColor: [220, 38, 38],
            },
          },
          {
            content: displayCount(totals.totalResolved, true),
            styles: { halign: "center", fontStyle: "bold", fillColor: "#f8f9fa" },
          },
          {
            content: displayCount(totals.pending, true),
            styles: { halign: "center", fontStyle: "bold", fillColor: "#f8f9fa" },
          },
        ]);
      } else {
        body.push([
          {
            content: "No Data Found",
            colSpan: 6,
            styles: { halign: "center", textColor: [255, 0, 0], fontStyle: "bold" },
          },
        ]);
      }


      const pdf = new jsPDF('landscape', 'mm', 'a4', { compressPdf: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const logoWidth = companyCode === '12' || companyCode === '13' || companyCode === '11' || companyCode === '16' || companyCode === '27' || companyCode === '48' || companyCode === '59' ? 25 : 30;
      const logoHeight = companyCode === '12' || companyCode === '13' || companyCode === '11' || companyCode === '16' || companyCode === '27' || companyCode === '48' || companyCode === '59' ? 20 : 10;
      pdf.addImage(getLogo(companyCode), 'PNG', 15, 5, logoWidth, logoHeight);

      const eOpsLogoWidth = 20;
      const eOpsLogoHeight = 8;
      const eOpsLogoXPosition = companyCode === '12' || companyCode === '13' || companyCode === '11' || companyCode === '16' || companyCode === '27' || companyCode === '48' || companyCode === '59' ? 43 : 17;
      pdf.addImage(eOpsLogo, 'PNG', eOpsLogoXPosition, companyCode === '12' || companyCode === '13' || companyCode === '11' || companyCode === '16' || companyCode === '27' || companyCode === '48' || companyCode === '59' ? 12 : 17, eOpsLogoWidth, eOpsLogoHeight);

      pdf.setFontSize(10);
      // pdf.text(convertDatewithTime(new Date()), pageWidth - 50, 12);
      // pdf.text("Name: " + name, pageWidth - 50, 17);
      // pdf.text("Designation: " + roleName, pageWidth - 50, 22);

      pdf.text(convertDatewithTime(new Date()), pageWidth - 65, 12);
      pdf.text("Name: " + name, pageWidth - 65, 17);
      pdf.text("Designation: " + roleName, pageWidth - 65, 22);

      pdf.setFontSize(12);
      pdf.text(getCompanies(companyCode), pageWidth / 2, 12, { align: 'center' });
      pdf.text(`APMS Resolved Complaint Stats - ${companyShortName(String(reportCompanyCode))}`, pageWidth / 2, 18, { align: 'center' });
      pdf.setFontSize(10);
      pdf.text(title, pageWidth / 2, 25, { align: 'center' });

      pdf.autoTable({
        startY: 28,
        head: headers,
        body: body,
        styles: { cellPadding: 1, fontSize: 8, halign: "center", lineWidth: 0.25, lineColor: "white" },
        margin: { top: 2 },
        theme: "striped",
        headStyles: { valign: "middle", fillColor: "#2980ba" },
        columnStyles: { 0: { halign: "left" } },
      });

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 45, pageHeight - 10);
      }

      if (print) {
        pdf.save(`${title}.pdf`);
      } else {
        resolve(pdf);
      }
    });
  };

  const exportToExcel = () => {
    const excelOfficeCodeLabel = "Office";
    const title =
      dateFrom && dateTo && convertDate(dateFrom) === convertDate(dateTo)
        ? `APMS Resolved Complaint Stats as on ${convertDate(dateTo)}`
        : `APMS Resolved Complaint Stats from ${dateFrom ? convertDate(dateFrom) : "-"} to ${dateTo ? convertDate(dateTo) : "-"}`;
    const table = tableRef.current;
    const wb = XLSX.utils.table_to_book(table, { sheet: 'Sheet1' });
    const ws = wb.Sheets['Sheet1'];

    const headers = [[
      excelOfficeCodeLabel,
      "Total",
      "Resolved (In Time)",
      "Resolved (Late)",
      "Total Resolved",
      "Pending",
    ]];

    const headingText = title;
    XLSX.utils.sheet_add_aoa(ws, [[headingText]], { origin: "A1" });

    const range = XLSX.utils.decode_range(ws["!ref"]);
    const columnCount = range.e.c - range.s.c + 1;

    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columnCount - 1 } }];

    if (!ws["A1"]) ws["A1"] = {};
    ws["A1"].s = { alignment: { horizontal: "center", vertical: "center" }, font: { bold: true } };

    XLSX.utils.sheet_add_aoa(ws, headers, { origin: "A2" });

    boldRows(ws, 1, 1);

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout]), `${title}.xlsx`);
  };

  const printPDF = async () => {
    let pdf = await DownloadPdf(false);
    const pdfUrl = pdf.output('bloburl');

    // Create an iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);

    // Create a cancel button
    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.position = 'fixed';
    cancelButton.style.top = '20px';
    cancelButton.style.right = '20px';
    cancelButton.style.padding = '10px 20px';
    cancelButton.style.backgroundColor = '#ff4d4d';
    cancelButton.style.color = 'white';
    cancelButton.style.border = 'none';
    cancelButton.style.borderRadius = '5px';
    cancelButton.style.cursor = 'pointer';
    document.body.appendChild(cancelButton);

    // Print the PDF when the iframe loads
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    };

    // Remove iframe and cancel button when cancel button is clicked
    cancelButton.addEventListener('click', () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      if (cancelButton.parentNode) {
        document.body.removeChild(cancelButton);
      }
    });
  };

  function getCurrentDateFormatted(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const options = { month: 'short' };
    const month = date.toLocaleString('en-US', options);
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  }

  // Fetch subDivisions for code == 10
  useEffect(() => {
    if (code == 10 && selectedCompanyCode && selectedCompanyCode !== "10") {
      const fetchSubDivisions = async () => {
        setViewBackdrop(true);
        const myHeaders = new Headers();
        myHeaders.append('Authorization', token);
        const formdata = new FormData();
        formdata.append('companyCode', selectedCompanyCode);
        const requestOptions = {
          method: 'POST',
          headers: myHeaders,
          body: formdata,
          redirect: 'follow',
        };
        try {
          const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
          if (response.ok) {
            const result = await response.json();
            if (result.status === '200') {
              setSubDivisions(result.message);
              setApiData(result.message);
              setFilteredCircles(buildUniqueCircles(result.message));
              setFilteredDivisions(buildUniqueDivisions(result.message));
              setFilteredSubDivisions(buildUniqueSubDivisions(result.message));
            } else {
              setSubDivisions([]);
              setFilteredCircles([]);
              setFilteredDivisions([]);
              setFilteredSubDivisions([]);
            }
          } else {
            throw new Error(`Request failed with status ${response.status}`);
          }
        } catch (error) {
          setSubDivisions([]);
          setFilteredCircles([]);
          setFilteredDivisions([]);
          setFilteredSubDivisions([]);
        } finally {
          setViewBackdrop(false);
        }
      };
      fetchSubDivisions();
    } else if (code != 10) {
      setFilteredCircles(buildUniqueCircles(subDivisions));
      setFilteredDivisions(buildUniqueDivisions(subDivisions));
      setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
    }
  }, [code, selectedCompanyCode, token, cfpBaseUrl]);

  const handleChangeCompany = async (e) => {
    const selectedCompany = e.target.value;
    setSelectedCompanyCode(selectedCompany);

    if (code == 10 && selectedCompany === "10") {
      setOfficeCode("10");
      setOfficeName("PITC");
      setCircleCode('');
      setSelectedDivision('');
      setSelectedSubDivision('');
      setCircleName('');
      setFilteredDivisions([]);
      setFilteredSubDivisions([]);
      setFilteredCircles([]);
      return;
    }

    setOfficeCode(selectedCompany);
    setOfficeName(getCompanies(selectedCompany) || '');

    if (code == 10 && selectedCompany) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const formdata = new FormData();
      formdata.append('companyCode', selectedCompany);
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      try {
        const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
        if (response.ok) {
          const result = await response.json();
          if (result.status === '200') {
            setSubDivisions(result.message);
            setApiData(result.message);

            // Reset dropdowns
            setCircleCode('');
            setSelectedDivision('');
            setSelectedSubDivision('');
            setCircleName('');

            setFilteredCircles(buildUniqueCircles(result.message));
            setFilteredDivisions(buildUniqueDivisions(result.message));
            setFilteredSubDivisions(buildUniqueSubDivisions(result.message));
          } else {
            setSubDivisions([]);
            setFilteredCircles([]);
            setFilteredDivisions([]);
            setFilteredSubDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredCircles([]);
        setFilteredDivisions([]);
        setFilteredSubDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (!selectedCompany) {
      // Reset when no company is selected
      setOfficeName('PITC');
      setOfficeCode('10');
      setCircleCode('');
      setSelectedDivision('');
      setSelectedSubDivision('');
      setCircleName('');
      setFilteredDivisions([]);
      setFilteredSubDivisions([]);
      setFilteredCircles([]);
      setSubDivisions([]);
      setApiData([]);
    }
  };

  const handleCircleChange = async (event) => {
    const selectedCircleCode = event.target.value ? parseInt(event.target.value) : '';
    if (code == 10 && selectedCircleCode && selectedCompanyCode) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const formdata = new FormData();
      formdata.append('companyCode', selectedCompanyCode);
      formdata.append('circleCode', selectedCircleCode);
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      try {
        const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
        if (response.ok) {
          const result = await response.json();
          if (result.status === '200') {
            setSubDivisions(result.message);
            setApiData(result.message);

            setCircleCode(selectedCircleCode);
            setOfficeCode(selectedCircleCode);
            const circle = filteredCircles.find(circle => circle.circleCode === selectedCircleCode);
            const circleName = circle?.circleName || '';
            setOfficeName(circleName.concat(' Circle'));
            setCircleName(circleName);
            setSelectedDivision('');
            setSelectedSubDivision('');
            setFilteredSubDivisions([]);

            // Populate divisions
            const uniqueDivisions = result.message
              .filter(item => item.circleCode === selectedCircleCode)
              .reduce((acc, item) => {
                if (!acc.some(division => division.divisionCode === item.divisionCode)) {
                  acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
                }
                return acc;
              }, []);
            setFilteredDivisions(uniqueDivisions);
          } else {
            setSubDivisions([]);
            setFilteredDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (code == 10 && !selectedCircleCode && selectedCompanyCode) {
      // Handle "All Circle" selection for code == 10
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const formdata = new FormData();
      formdata.append('companyCode', selectedCompanyCode);
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      try {
        const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
        if (response.ok) {
          const result = await response.json();
          if (result.status === '200') {
            setSubDivisions(result.message);
            setApiData(result.message);

            setCircleCode('');
            setOfficeCode(selectedCompanyCode);
            setOfficeName(getCompanies(selectedCompanyCode));
            setCircleName('');
            setSelectedDivision('');
            setSelectedSubDivision('');
            setFilteredSubDivisions([]);

            // Populate divisions
            const uniqueDivisions = result.message.reduce((acc, item) => {
              if (!acc.some(division => division.divisionCode === item.divisionCode)) {
                acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
              }
              return acc;
            }, []);
            setFilteredDivisions(uniqueDivisions);
          } else {
            setSubDivisions([]);
            setFilteredDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (selectedCircleCode) {
      setCircleCode(selectedCircleCode);
      setOfficeCode(selectedCircleCode);
      const circle = filteredCircles.find(circle => circle.circleCode === selectedCircleCode);
      const circleName = circle?.circleName || '';
      setOfficeName(circleName.concat(' Circle'));
      setCircleName(circleName);
      setSelectedDivision('');
      setSelectedSubDivision('');
      setFilteredSubDivisions([]);
      const uniqueDivisions = subDivisions
        .filter(item => item.circleCode === selectedCircleCode)
        .reduce((acc, item) => {
          if (!acc.some(division => division.divisionCode === item.divisionCode)) {
            acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
          }
          return acc;
        }, []);
      setFilteredDivisions(uniqueDivisions);
    } else {
      // Handle "All Circle" for code != 10
      setCircleCode('');
      setSelectedDivision('');
      setSelectedSubDivision('');
      setFilteredSubDivisions([]);
      if (roleTypeName == 'Technical') {
        if (offCode) {
          setOfficeCode(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode));
          let officeName = getName(String(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode)), subDivisions);
          setOfficeName(officeName.concat(' Region'));
          setCircleName('');
          const circles = subDivisions.reduce((acc, curr) => {
            if (!acc.some(item => item.circleCode === curr.circleCode)) {
              acc.push({ circleCode: curr.circleCode, circleName: curr.circleName, companyCode: curr.companyCode });
            }
            return acc;
          }, []);
          setFilteredCircles(circles);
          const uniqueDivisions = subDivisions.reduce((acc, item) => {
            if (!acc.some(division => division.divisionCode === item.divisionCode)) {
              acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
            }
            return acc;
          }, []);
          setFilteredDivisions(uniqueDivisions);
        }
      } else if (roleTypeName == 'Commercial') {
        if (cfcLevelId == 2) {
          if (offCode) {
            setOfficeCode(offCode.toString().slice(0, 2));
            let offName = cscID;
            setOfficeName(offName);
            setHierarchy(cfcLevelId);
            const uniqueDivisions = subDivisions.reduce((acc, item) => {
              if (!acc.some(division => division.divisionCode === item.divisionCode)) {
                acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
              }
              return acc;
            }, []);
            setFilteredDivisions(uniqueDivisions);
          }
        } else if (cfcLevelId == 3) {
          if (offCode) {
            setOfficeCode(offCode.toString().slice(0, 3));
            let offName = cscID;
            setOfficeName(offName);
            setHierarchy(cfcLevelId);
            const uniqueDivisions = subDivisions.reduce((acc, item) => {
              if (!acc.some(division => division.divisionCode === item.divisionCode)) {
                acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
              }
              return acc;
            }, []);
            setFilteredDivisions(uniqueDivisions);
          }
        }
      }
    }
  };

  const handleDivisionChange = async (e) => {
    const selectedDivisionCode = parseInt(e.target.value);
    if (code == 10 && selectedDivisionCode && selectedCompanyCode) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const formdata = new FormData();
      formdata.append('companyCode', selectedCompanyCode);
      formdata.append('divisionCode', selectedDivisionCode);
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      try {
        const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
        if (response.ok) {
          const result = await response.json();
          if (result.status === '200') {
            setSubDivisions(result.message);
            setApiData(result.message);

            setSelectedDivision(selectedDivisionCode);
            setOfficeCode(selectedDivisionCode);
            const division = filteredDivisions.find(division => division.divisionCode === selectedDivisionCode);
            const divisionName = division?.divisionName || '';
            setOfficeName(divisionName.concat(' Division'));
            setSelectedSubDivision('');
            const uniqueSubDivisions = result.message
              .filter(item => item.divisionCode == selectedDivisionCode)
              .reduce((acc, item) => {
                if (!acc.some(sub => sub.subDivisionCode === item.subDivisionCode)) {
                  acc.push({ subDivisionCode: item.subDivisionCode, subDivisionName: item.subDivisionName, companyCode: item.companyCode });
                }
                return acc;
              }, []);
            setFilteredSubDivisions(uniqueSubDivisions);
          } else {
            setSubDivisions([]);
            setFilteredSubDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
        setFilteredSubDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (selectedDivisionCode) {
      setSelectedDivision(selectedDivisionCode);
      setOfficeCode(selectedDivisionCode);
      const divisionName = filteredDivisions.find(division => division.divisionCode === selectedDivisionCode)?.divisionName || '';
      setOfficeName(divisionName.concat(' Division'));
      setSelectedSubDivision('');
      const uniqueSubDivisions = subDivisions
        .filter(item => item.divisionCode == selectedDivisionCode)
        .reduce((acc, item) => {
          if (!acc.some(sub => sub.subDivisionCode === item.subDivisionCode)) {
            acc.push({ subDivisionCode: item.subDivisionCode, subDivisionName: item.subDivisionName, companyCode: item.companyCode });
          }
          return acc;
        }, []);
      setFilteredSubDivisions(uniqueSubDivisions);
    } else {
      setSelectedDivision('');
      setOfficeCode(circleCode ? circleCode : offCode);
      let officeName = getName(circleCode ? circleCode.toString() : offCode.toString(), subDivisions);
      setOfficeName(officeName.concat(' Circle'));
      setSelectedSubDivision('');
      setFilteredSubDivisions([]);
      const uniqueDivisions = subDivisions
        .filter(item => item.circleCode == (circleCode ? circleCode : offCode))
        .reduce((acc, item) => {
          if (!acc.some(division => division.divisionCode === item.divisionCode)) {
            acc.push({ divisionCode: item.divisionCode, divisionName: item.divisionName, companyCode: item.companyCode });
          }
          return acc;
        }, []);
      setFilteredDivisions(uniqueDivisions);
    }
  };

  const handleSubDivisionChange = async (e) => {
    const selectedSubDivisionCode = parseInt(e.target.value);
    if (code == 10 && selectedSubDivisionCode && selectedCompanyCode) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append('Authorization', token);
      const formdata = new FormData();
      formdata.append('companyCode', selectedCompanyCode);
      formdata.append('subDivisionCode', selectedSubDivisionCode);
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      try {
        const response = await fetch(cfpBaseUrl + 'customerFaciliationPortal/subDivWithCompDetails', requestOptions);
        if (response.ok) {
          const result = await response.json();
          if (result.status === '200') {
            setSubDivisions(result.message);
            setApiData(result.message);

            setSelectedSubDivision(selectedSubDivisionCode);
            setOfficeCode(selectedSubDivisionCode);
            const subDivision = filteredSubDivisions.find(sub => sub.subDivisionCode === selectedSubDivisionCode);
            const subDivisionName = subDivision?.subDivisionName || '';
            setOfficeName(subDivisionName.concat(' Sub Division'));
          } else {
            setSubDivisions([]);
          }
        } else {
          throw new Error(`Request failed with status ${response.status}`);
        }
      } catch (error) {
        setSubDivisions([]);
      } finally {
        setViewBackdrop(false);
      }
    } else if (selectedSubDivisionCode) {
      setSelectedSubDivision(selectedSubDivisionCode);
      setOfficeCode(selectedSubDivisionCode);
      const subDivisionName = filteredSubDivisions.find(sub => sub.subDivisionCode === selectedSubDivisionCode)?.subDivisionName || '';
      setOfficeName(subDivisionName.concat(' Sub Division'));
    } else {
      setSelectedSubDivision('');
      let officeName = getName(selectedDivision ? selectedDivision.toString() : offCode.toString(), subDivisions);
      setOfficeName(officeName.concat(' Division'));
      setOfficeCode(selectedDivision ? selectedDivision : offCode);
    }
  };

  useEffect(() => {
    if (roleTypeName == 'Technical') {
      if (userHierarchy == 52 || userHierarchy == 5) {
        if (code) {
          setOfficeCode(String(code).slice(0, 5));
          setOfficeName(getName(String(code), subDivisions));
          setHierarchy(5);
        }
      } else if (offCode) {
        setOfficeCode(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode));
        let offName = getName(String(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode)), subDivisions);
        setOfficeName(offName);
        setHierarchy(offCode.toString().length);
      }
    } else if (roleTypeName == 'Commercial') {
      if (userHierarchy == 52 || userHierarchy == 5) {
        if (code) {
          setOfficeCode(String(code).slice(0, 5));
          setOfficeName(cscID || getName(code, subDivisions));
          setHierarchy(5);
        }
      } else if (cfcLevelId == 1) {
        if (offCode) {
          if (offCode == 10) {
            const effectiveCompany =
              selectedCompanyCode || selectedAdminCompanyCode || offCode;
            setOfficeCode(effectiveCompany);
            setOfficeName(getCompanies(String(effectiveCompany)) || "PITC");
          } else {
            setOfficeCode(offCode);
            let offName = getName(offCode, subDivisions);
            setOfficeName(offName);
          }

          setHierarchy(offCode.length);
        }
      } else if (cfcLevelId == 2) {
        if (offCode) {
          setOfficeCode(offCode.toString().slice(0, 2));
          let offName = cscID;
          setOfficeName(offName);
          setHierarchy(cfcLevelId);
        }
      } else if (cfcLevelId == 3) {
        if (offCode) {
          setOfficeCode(offCode.toString().slice(0, 3));
          let offName = cscID;
          setOfficeName(offName);
          setHierarchy(cfcLevelId);
        }
      } else if (cfcLevelId == 4) {
                if (offCode) {
                    setOfficeCode(offCode.toString().slice(0, 4));
                    let offName = cscID;
                    setOfficeName(offName);
                    setHierarchy(cfcLevelId);
                }
            }else if (cfcLevelId == 5) {
                if (offCode) {
                    setOfficeCode(offCode.toString().slice(0, 5));
                    let offName = cscID;
                    setOfficeName(offName);
                    setHierarchy(cfcLevelId);
                }
            }
    }
  }, [offCode, cfcLevelId, roleTypeName, cscID, selectedAdminCompanyCode, selectedCompanyCode, userHierarchy, code, subDivisions]);

  useEffect(() => {
    if (hierarchy) {
      if (hierarchy == 1 || cfcLevelId === 1) {
        setFilteredCircles(buildUniqueCircles(subDivisions));
        setFilteredDivisions(buildUniqueDivisions(subDivisions));
        setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
      } else if (hierarchy == 2 || cfcLevelId == 2) {
        setFilteredCircles(buildUniqueCircles(subDivisions));
        setFilteredDivisions(buildUniqueDivisions(subDivisions));
        setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
      } else if (hierarchy == 3 || cfcLevelId == 3) {
        applyScopedHierarchyFilterLists({
          items: subDivisions,
          effectiveHierarchy: 3,
          authCode: offCode,
          setCircleCode,
          setSelectedDivision,
          setSelectedSubDivision,
          setFilteredCircles,
          setFilteredDivisions,
          setFilteredSubDivisions,
        });
      } else if (hierarchy == 4 || cfcLevelId == 4) {
        applyScopedHierarchyFilterLists({
          items: subDivisions,
          effectiveHierarchy: 4,
          authCode: offCode,
          setCircleCode,
          setSelectedDivision,
          setSelectedSubDivision,
          setFilteredCircles,
          setFilteredDivisions,
          setFilteredSubDivisions,
        });
      } else if (
        hierarchy == 5 ||
        cfcLevelId == 5 ||
        userHierarchy == 52
      ) {
        applyScopedHierarchyFilterLists({
          items: subDivisions,
          effectiveHierarchy: 5,
          authCode: offCode,
          setCircleCode,
          setSelectedDivision,
          setSelectedSubDivision,
          setFilteredCircles,
          setFilteredDivisions,
          setFilteredSubDivisions,
        });
      }
    }
  }, [hierarchy, cfcLevelId, subDivisions, userHierarchy]);

  const isAdminUser = String(code) === "10";
  const effectiveHierarchy = getEffectiveUserHierarchy(
    hierarchy,
    cfcLevelId,
    userHierarchy,
    code,
  );
  const availableReportLevelOptions = getAvailableReportLevelOptions(
    code,
    effectiveHierarchy,
  );
  const isHierarchicalReportLevel =
    isHierarchicalReportLevelValue(reportLevel);
  const authOfficeCode = resolveAuthOfficeCode(code, effectiveHierarchy);

  useEffect(() => {
    if (!availableReportLevelOptions.length) {
      return;
    }

    const isCurrentLevelAllowed = availableReportLevelOptions.some(
      (option) => option.value === reportLevel,
    );

    if (!isCurrentLevelAllowed) {
      const nextLevel =
        availableReportLevelOptions[0]?.value ||
        getDefaultReportLevel(code, effectiveHierarchy);
      setReportLevel(nextLevel);
    }
  }, [availableReportLevelOptions, effectiveHierarchy, code]);

  const resetHierarchyFiltersToAuthScope = () => {
    if (effectiveHierarchy >= 5) {
      setSelectedSubDivision("");
    } else if (effectiveHierarchy === 4) {
      setSelectedSubDivision("");
    } else if (effectiveHierarchy === 3) {
      setSelectedDivision("");
      setSelectedSubDivision("");
      setFilteredSubDivisions([]);
    } else {
      setCircleCode("");
      setCircleName("");
      setSelectedDivision("");
      setSelectedSubDivision("");
      setFilteredDivisions([]);
      setFilteredSubDivisions([]);
    }
  };

  const restoreHierarchyScopedFilters = () => {
    if (effectiveHierarchy >= 3) {
      applyScopedHierarchyFilterLists({
        items: subDivisions,
        effectiveHierarchy,
        authCode: offCode,
        setCircleCode,
        setSelectedDivision,
        setSelectedSubDivision,
        setFilteredCircles,
        setFilteredDivisions,
        setFilteredSubDivisions,
      });
    }
  };

  const handleReportLevelChange = (event) => {
    const nextLevel = event.target.value;
    const level = Number(nextLevel);
    setReportLevel(nextLevel);

    if (isAdminUser && level !== 6) {
      setCircleCode("");
      setCircleName("");
      setSelectedDivision("");
      setSelectedSubDivision("");
      setFilteredSubDivisions([]);
      const company =
        selectedCompanyCode || String(selectedAdminCompanyCode) || "10";
      setOfficeCode(company);
      setOfficeName(company === "10" ? "PITC" : getCompanies(company) || "");
      setFilteredDivisions(buildUniqueDivisions(subDivisions));
      setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
      return;
    }

    if (!isAdminUser && level !== 6) {
      resetHierarchyFiltersToAuthScope();
      setOfficeCode(authOfficeCode);
      setOfficeName(getName(authOfficeCode, subDivisions) || "");
      return;
    }

    if (isAdminUser && level === 6 && subDivisions.length > 0) {
      setFilteredCircles(buildUniqueCircles(subDivisions));
      setFilteredDivisions(buildUniqueDivisions(subDivisions));
      setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
      return;
    }

    if (!isAdminUser && level === 6) {
      resetHierarchyFiltersToAuthScope();
      restoreHierarchyScopedFilters();
      setOfficeCode(authOfficeCode);
      setOfficeName(getName(authOfficeCode, subDivisions) || "");

      if (effectiveHierarchy <= 2 && subDivisions.length > 0) {
        setFilteredCircles(buildUniqueCircles(subDivisions));
        setFilteredDivisions(buildUniqueDivisions(subDivisions));
        setFilteredSubDivisions(buildUniqueSubDivisions(subDivisions));
      }
    }
  };

  useEffect(() => {
    if (isAdminUser || !isHierarchicalReportLevelValue(reportLevel)) {
      return;
    }

    if (effectiveHierarchy >= 3) {
      applyScopedHierarchyFilterLists({
        items: subDivisions,
        effectiveHierarchy,
        authCode: offCode,
        setCircleCode,
        setSelectedDivision,
        setSelectedSubDivision,
        setFilteredCircles,
        setFilteredDivisions,
        setFilteredSubDivisions,
      });
    }
  }, [
    reportLevel,
    effectiveHierarchy,
    subDivisions,
    offCode,
    isAdminUser,
  ]);

  const showCircleFilter =
    isHierarchicalReportLevel && (isAdminUser || effectiveHierarchy <= 2);
  const showDivisionFilter =
    isHierarchicalReportLevel && (isAdminUser || effectiveHierarchy <= 3);
  const showSubDivisionFilter =
    isHierarchicalReportLevel && (isAdminUser || effectiveHierarchy <= 4);

  const circleFilterEnabled =
    isHierarchicalReportLevel && (isAdminUser || effectiveHierarchy <= 2);
  const divisionFilterEnabled = isHierarchicalReportLevel
    ? isAdminUser || effectiveHierarchy === 2
      ? Boolean(circleCode)
      : effectiveHierarchy === 3
    : false;
  const subDivisionFilterEnabled = isHierarchicalReportLevel
    ? effectiveHierarchy >= 4 || Boolean(selectedDivision)
    : false;

  const handleGoClick = () => {
    const apiOfficeCode = resolveApmsReportOfficeCode({
      userCode: code,
      effectiveHierarchy,
      reportLevelValue: reportLevel,
      officeCode,
      selectedCompanyCode,
      selectedAdminCompanyCode,
      circleCode,
      selectedDivision,
      selectedSubDivision,
    });
    setAppliedReportOfficeCode(String(apiOfficeCode || ""));
    setAppliedReportLevel(reportLevel);
    fetchData(apiOfficeCode, dateFrom, dateTo, reportLevel);

    setLastAppliedFilters({ ...currentFilters });

    setIsGoDisabled(true);
  };

  const connectionTypes = [
    officeCodeColumnLabel,
    "Total",
    "Resolved (In Time)",
    "Resolved (Late)",
    "Total Resolved",
    "Pending",
  ];

  const fetchData = async (officeCode, dateFrom, dateTo, level) => {
    setViewBackdrop(true);

    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      officeCode: officeCode?.toString() || code.slice(0, 2),
      dateFrom,
      dateTo,
      reportLevel: Number(level) || 2,
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        cfpBaseUrl + "apms/getAPMSResolvedComplaintsStats",
        requestOptions
      );
      const result = await response.json();

      if (result.status === "200" && Array.isArray(result.data)) {
        const normalizedRows = result.data.map((item) => {
          const row = normalizeApmsSubDivRow(item);
          return {
            ...row,
            officeDisplayName: resolveOfficeDisplayName(
              row.subDivisionCode,
              subDivisions
            ),
          };
        });
        normalizedRows.sort((a, b) =>
          String(a.subDivisionCode).localeCompare(String(b.subDivisionCode))
        );
        setTableData(normalizedRows);
      } else {
        setTableData([]);
      }
    } catch (error) {
      setTableData([]);
    }

    setViewBackdrop(false);
  };




  function formatWithCommas(value) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '5px',
    textAlign: 'center',
    fontSize: '15px',
  };


  const thTdStyle = {
    border: 'none',
    padding: '0px 8px',
    fontSize: '14px'
  };

  const subdivthTdStyle = {
    border: 'none',
    padding: '6px 7px',
    backgroundColor: '#d5e5fc',
    fontSize: '15px',
    fontWeight: 'bold'
  };

  const headerStyle = {
    backgroundColor: '#eeeff0',
    border: 'none',
    fontSize: '15px',
    paddingTop: '10px' // Extra space above headers
  };

  const currentFilters = {
      officeCode, dateFrom, dateTo, reportLevel
  };

  useEffect(() => {
    if (lastAppliedFilters === null) {
      setIsGoDisabled(false);
      return;
    }

    const hasChanged = JSON.stringify(currentFilters) !== JSON.stringify(lastAppliedFilters);
    setIsGoDisabled(!hasChanged);
  }, [
    officeCode, dateFrom, dateTo, reportLevel
  ]);

  const noDataAvailable = () => {
    alert("No data available to export or print.");
  };

  const reportOfficeLabel = officeName || "All Offices";
  const tableCaptionText =
    dateFrom && dateTo && convertDate(dateFrom) === convertDate(dateTo)
      ? `APMS Resolved Complaint Stats - ${companyShortName(String(reportCompanyCode))} as on ${convertDate(dateTo)}`
      : `APMS Resolved Complaint Stats - ${companyShortName(String(reportCompanyCode))} from ${dateFrom ? convertDate(dateFrom) : "-"} to ${dateTo ? convertDate(dateTo) : "-"}`;

  const tableTotals = tableData.reduce(
    (acc, item) => ({
      total: acc.total + (item.total || 0),
      resolvedInTime: acc.resolvedInTime + (item.resolvedInTime || 0),
      resolvedLate: acc.resolvedLate + (item.resolvedLate || 0),
      totalResolved: acc.totalResolved + (item.totalResolved || 0),
      pending: acc.pending + (item.pending || 0),
    }),
    {
      total: 0,
      resolvedInTime: 0,
      resolvedLate: 0,
      totalResolved: 0,
      pending: 0,
    }
  );

  return (
    <>
      <Box className="form-box rpt-page coa-status-report swacs-page">
        <div className="coa-layout">
          <Box className="application-table" style={{ marginRight: "0px", width: "100%" }}>
            <header className="coa-page-header">
              <h1 className="coa-page-title">APMS Resolved Complaint Stats</h1>
            </header>
            <div className="div-datagrid">
              <div className="coa-toolbar-stack">
                <Paper
                  variant="outlined"
                  className="coa-card coa-card--filters"
                  sx={{
                    p: 2,
                    width: "100%",
                    bgcolor: "#fff",
                    borderRadius: 2,
                    borderColor: "divider",
                  }}
                >
                  <div
                    className="coa-filters coa-filters--mui-grid"
                    style={{ width: "100%" }}
                  >
                    <Grid container spacing={2} alignItems="flex-end">


                      <Grid item xs={12} sm={6} md={2}>
                        <Stack spacing={0.5}>
                          <Typography className="field-label">Date From</Typography>
                          <input
                            type="date"
                            className="date-field cs-filter-input"
                            value={dateFrom}
                            max={getTodayIsoDate()}
                            onChange={(e) => {
                              setDateFrom(clampIsoDateToToday(e.target.value));
                            }}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Stack spacing={0.5}>
                          <Typography className="field-label">Date To</Typography>
                          <input
                            type="date"
                            className="date-field cs-filter-input"
                            value={dateTo}
                            max={getTodayIsoDate()}
                            onChange={(e) => {
                              setDateTo(clampIsoDateToToday(e.target.value));
                            }}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Stack spacing={0.5}>
                          <Typography className="field-label">Report Level</Typography>
                          <select
                            className="dropdown-field cs-filter-input"
                            value={reportLevel}
                            onChange={handleReportLevelChange}
                            disabled={availableReportLevelOptions.length <= 1}
                          >
                            {availableReportLevelOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </Stack>
                      </Grid>
                      {code == 10 && (
                        <Grid item xs={12} sm={6} md={2}>
                          <Stack spacing={0.5}>
                            <Typography className="field-label">Company</Typography>
                            <select
                              className="dropdown-field cs-filter-input"
                              value={selectedCompanyCode}
                              onChange={handleChangeCompany}
                            >
                        <option value="">Select Company</option>
                        <option value="10">PITC</option>
                        <option value="11">LESCO</option>
                        <option value="12">GEPCO</option>
                        <option value="13">FESCO</option>
                        <option value="14">IESCO</option>
                        <option value="15">MEPCO</option>
                        <option value="26">PESCO</option>
                        <option value="27">HAZECO</option>
                        <option value="37">HESCO</option>
                        <option value="38">SEPCO</option>
                        <option value="48">QESCO</option>
                        <option value="59">TESCO</option>
                            </select>
                          </Stack>
                        </Grid>
                      )}

                      {showCircleFilter && (
                        <Grid item xs={12} sm={6} md={2}>
                          <Stack spacing={0.5}>
                            <Typography className="field-label">Circle</Typography>
                            <select
                              className="dropdown-field cs-filter-input"
                              value={circleCode || ""}
                              onChange={handleCircleChange}
                              disabled={!circleFilterEnabled}
                            >
                        <option value="">All Circle</option>
                        {filteredCircles.map((circle) => (
                          <option key={circle.circleCode} value={circle.circleCode}>
                            {circle.circleName}
                          </option>
                        ))}
                            </select>
                          </Stack>
                        </Grid>
                      )}

                      {showDivisionFilter && (
                        <Grid item xs={12} sm={6} md={2}>
                          <Stack spacing={0.5}>
                            <Typography className="field-label">Division</Typography>
                            <select
                              className="dropdown-field cs-filter-input"
                              value={selectedDivision || ""}
                              onChange={handleDivisionChange}
                              disabled={!divisionFilterEnabled}
                            >
                        <option value="">All Division</option>
                        {filteredDivisions.map((division) => (
                          <option key={division.divisionCode} value={division.divisionCode}>
                            {division.divisionName}
                          </option>
                        ))}
                            </select>
                          </Stack>
                        </Grid>
                      )}

                      {showSubDivisionFilter && (
                        <Grid item xs={12} sm={6} md={2}>
                          <Stack spacing={0.5}>
                            <Typography className="field-label">Sub-Division</Typography>
                            <select
                              className="dropdown-field cs-filter-input"
                              value={selectedSubDivision || ""}
                              onChange={handleSubDivisionChange}
                              disabled={!subDivisionFilterEnabled}
                            >
                        <option value="">All Sub-Division</option>
                        {filteredSubDivisions.map((subDivision) => (
                          <option key={subDivision.subDivisionCode} value={subDivision.subDivisionCode}>
                            {subDivision.subDivisionName}
                          </option>
                        ))}
                            </select>
                          </Stack>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6} md={2}>
                        <Stack spacing={0.5}>
                          <Box
                            aria-hidden
                            sx={{
                              height: "1.35rem",
                              mb: 0.5,
                              lineHeight: 1.35,
                            }}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            className="coa-go-btn"
                            onClick={handleGoClick}
                            disabled={isGoDisabled || viewBackdrop}
                          >
                            GO
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </div>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    alignItems="center"
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    spacing={1}
                    className="coa-export-strip"
                    sx={{
                      pt: 2,
                      mt: 2,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      gap: 1,
                    }}
                  >
                    <ReportExportButtons
                      isEmpty={tableData.length === 0}
                      onEmpty={noDataAvailable}
                      onPdf={() => DownloadPdf(true)}
                      onExcel={exportToExcel}
                      onPrint={printPDF}
                    />
                  </Stack>
                </Paper>
              </div>
              <div className="coa-report-banner" role="status">
                {tableCaptionText}
              </div>
              <div className="table-container" style={{ marginBottom: "40px" }}>
                <div id="pdf-table">
                  <div className="coa-table-scroll">
                    <table
                      className="table"
                      ref={tableRef}
                      style={{
                        marginBottom: "40px",
                        width: "100%",
                        borderCollapse: "collapse",
                      }}
                    >
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          backgroundColor: "white",
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          {connectionTypes.map((type, index) => (
                            <th key={index}>{type}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableData && tableData.length > 0 ? (
                          <>
                            {tableData.map((item, id) => (
                              <tr
                                key={id}
                                style={{
                                  backgroundColor:
                                    id % 2 === 0 ? "#f8f9fa" : "#e3efff",
                                }}
                              >
                                <td style={{ textAlign: "left" }}>
                                  {item.officeDisplayName || item.subDivisionCode || "-"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {displayCount(item.total, true)}
                                </td>
                                <td
                                  className="swacs-resolved-in-time"
                                  style={{ textAlign: "center" }}
                                >
                                  {displayCount(item.resolvedInTime)}
                                </td>
                                <td
                                  className="swacs-resolved-late"
                                  style={{ textAlign: "center" }}
                                >
                                  {displayCount(item.resolvedLate)}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {displayCount(item.totalResolved, true)}
                                </td>
                                <td style={{ textAlign: "center", fontWeight: 700 }}>
                                  {displayCount(item.pending, true)}
                                </td>
                              </tr>
                            ))}
                            <tr className="swacs-total-row">
                              <td style={{ textAlign: "left", fontWeight: "bold" }}>
                                {companyTotalLabel}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: "bold" }}>
                                {displayCount(tableTotals.total, true)}
                              </td>
                              <td
                                className="swacs-resolved-in-time"
                                style={{ textAlign: "center", fontWeight: "bold" }}
                              >
                                {displayCount(tableTotals.resolvedInTime, true)}
                              </td>
                              <td
                                className="swacs-resolved-late"
                                style={{ textAlign: "center", fontWeight: "bold" }}
                              >
                                {displayCount(tableTotals.resolvedLate, true)}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: "bold" }}>
                                {displayCount(tableTotals.totalResolved, true)}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: "bold" }}>
                                {displayCount(tableTotals.pending, true)}
                              </td>
                            </tr>
                          </>
                        ) : !viewBackdrop ? (
                          <tr className="swacs-empty-row">
                            <td colSpan={connectionTypes.length}>
                              No Data Found
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                open={viewBackdrop}
              >
                <AppLoader />
              </Backdrop>







            </div>
          </Box>
        </div>
      </Box>

    </>
  )
}

export default SubdivisionWiseAPMSComplaintSummary;